import type { DecisionListType, DecisionRow } from "@/api/client.js";
import { HelpTip } from "@/components/HelpTip.js";
import { Button } from "@/components/ui/button.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.js";
import { tooltips } from "@/copy/tooltips.js";
import { useIPFilter } from "@/features/ip-filter/IPFilterContext.js";
import { isValidIp, matchDecisions, type DecisionMatchScope } from "@/features/ip-filter/match.js";

type Props = {
  data: DecisionRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  listType: DecisionListType;
  matchScope: DecisionMatchScope;
  onUnban: (row: DecisionRow) => void;
  onUseIp: (ip: string) => void;
};

function rowSupportsUnban(r: DecisionRow): boolean {
  return r.type === undefined || r.type === "" || r.type === "ban";
}

function rowSupportsWhitelistIp(r: DecisionRow): boolean {
  return isValidIp(r.value ?? "");
}

export function DecisionsTable({
  data,
  isLoading,
  isError,
  error,
  listType,
  matchScope,
  onUnban,
  onUseIp,
}: Props) {
  const { query } = useIPFilter();
  const rows = data ?? [];
  const q = query.trim();
  const { exact, partial } = matchDecisions(rows, q, { matchScope });
  const highlight = new Set([...exact, ...partial].map((r) => r.id));

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="decisions-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-destructive" data-testid="decisions-error">
        {error?.message ?? "Failed to load decisions"}
      </p>
    );
  }

  if (rows.length === 0) {
    const emptyMsg =
      listType === "all" ? "No decisions in the current list." : "No active bans in the current list.";
    return <p className="text-muted-foreground">{emptyMsg}</p>;
  }

  return (
    <Table data-testid="decisions-table">
      <TableHeader>
        <TableRow>
          <TableHead>Value</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Scenario</TableHead>
          <TableHead>Until</TableHead>
          <TableHead>Id</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={r.id}
            data-matched={highlight.has(r.id) ? "true" : undefined}
            className={highlight.has(r.id) ? "bg-primary/10" : undefined}
          >
            <TableCell>{r.value}</TableCell>
            <TableCell>{r.type ?? "—"}</TableCell>
            <TableCell>{r.scenario ?? r.reason}</TableCell>
            <TableCell>{r.until ?? r.duration}</TableCell>
            <TableCell>{r.id}</TableCell>
            <TableCell className="space-x-2">
              <HelpTip content={tooltips.unban}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!rowSupportsUnban(r)}
                  title={!rowSupportsUnban(r) ? "Not a ban decision" : undefined}
                  onClick={() => onUnban(r)}
                >
                  Unban
                </Button>
              </HelpTip>
              <HelpTip content={tooltips.whitelistThisIp}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!rowSupportsWhitelistIp(r)}
                  title={!rowSupportsWhitelistIp(r) ? "Value is not a single IP" : undefined}
                  onClick={() => onUseIp(r.value ?? "")}
                >
                  Whitelist this IP
                </Button>
              </HelpTip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
